// src/pages/ProjectPage.js
import React, { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PROJECTS } from "../data/PortfolioData";
import "./ProjectPage.css";
import { Button } from "./Button";
import LightboxModal from "./LightboxModal";

// normalize YouTube links (shorts / watch) to embed
function normalizeYoutube(src = "") {
  try {
    const url = new URL(src);
    const host = url.hostname;
    let videoId = "";

    if (host.includes("youtube.com")) {
      if (url.pathname.startsWith("/shorts/")) {
        // shorts → /shorts/VIDEO_ID
        videoId = url.pathname.split("/")[2];
      } else if (url.pathname === "/watch") {
        // normal watch → /watch?v=VIDEO_ID
        videoId = url.searchParams.get("v");
      } else if (url.pathname.startsWith("/embed/")) {
        // already embed
        return src;
      }
    } else if (host.includes("youtu.be")) {
      // youtu.be/VIDEO_ID
      videoId = url.pathname.slice(1);
    }

    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
    }
    return src; // unchanged if not youtube
  } catch {
    return src;
  }
}

// choose the correct iframe permissions by domain
function iframeAllowFor(src = "") {
  try {
    const host = new URL(src).hostname;
    if (host.includes("youtube.com") || host.includes("youtu.be")) {
      return "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
    }
    if (host.includes("vimeo.com")) {
      return "autoplay; fullscreen; picture-in-picture";
    }
    // Kuula / 360 viewers (default)
    return "xr-spatial-tracking; gyroscope; accelerometer; fullscreen";
  } catch {
    // safe default
    return "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
  }
}

export default function ProjectPage() {
  const { slug } = useParams();

  const project = useMemo(
    () =>
      PROJECTS.find(
        (p) => (p.slug || "").replace(/^\/portfolio\//, "") === slug
      ),
    [slug]
  );

  // instead of early return, track not-found and always call hooks
  const notFound = !project;

  // safe destructuring with fallback values
  const {
    title = "Project",
    cover = "/images/placeholder.jpg",
    media = [],
    location = "",
    services = [],
    excerpt = "",
  } = project || {};

  // group images into one album; leave videos/iframes alone
  const images = useMemo(() => media.filter((m) => m.type === "image"), [media]);
  const nonImages = useMemo(() => media.filter((m) => m.type !== "image"), [media]);

  // index of the first image in the original media array, for correct lightbox start
  const firstImageIndex = useMemo(
    () => media.findIndex((m) => m.type === "image"),
    [media]
  );

  const [lbOpen, setLbOpen] = useState(false);
  const [lbIndex, setLbIndex] = useState(0);

  return (
    <main className="pp-wrap">
      {notFound ? (
        <div className="pp-notfound">
          <h1>Project not found</h1>
          <p>We couldn’t find this portfolio entry.</p>
          <Link to="/portfolio">
            <Button
              className="btn-mobile"
              buttonStyle="btn--outline"
              buttonSize="btn--large"
            >
              Back to Portfolio
            </Button>
          </Link>
        </div>
      ) : (
        <>
          {/* Hero */}
          <section className="pp-hero">
            <div className="pp-hero-media">
              <img src={cover} alt={title} className="pp-hero-img" />
            </div>
            <div className="pp-hero-meta">
              <h1>{title}</h1>
              {excerpt && <p className="pp-excerpt">{excerpt}</p>}

              <div className="pp-info">
                {location && (
                  <div className="pp-row">
                    <span className="pp-label">Location</span>
                    <span className="pp-value">{location}</span>
                  </div>
                )}
                {services.length > 0 && (
                  <div className="pp-row">
                    <span className="pp-label">Services</span>
                    <span className="pp-value">{services.join(" · ")}</span>
                  </div>
                )}
              </div>

              <div className="pp-actions">
                <Link to="/portfolio">
                  <Button
                    className="btn-mobile"
                    buttonStyle="btn--outline"
                    buttonSize="btn--medium"
                  >
                    ← Back to Portfolio
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    className="btn-mobile"
                    buttonStyle="btn--primary"
                    buttonSize="btn--medium"
                  >
                    Book a Shoot
                  </Button>
                </Link>
              </div>
            </div>
          </section>

          {/* Gallery */}
          {media.length > 0 && (
            <section className="pp-gallery">
              {/* non-images (videos/iframes) unchanged */}
              {nonImages.map((m, iNon) => {
                const origIndex = media.findIndex((x) => x === nonImages[iNon]);
                return (
                  <figure
                    key={`nonimg-${iNon}`}
                    className={`pp-item ${m.type}`}
                    title={m.type === "iframe" ? undefined : "Quick view"}
                    onClick={() => {
                      if (m.type !== "iframe") {
                        setLbIndex(origIndex);
                        setLbOpen(true);
                      }
                    }}
                  >
                    {m.type === "video" ? (
                      <video
                        src={m.src}
                        className="pp-media"
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : m.type === "iframe" ? (
                      <div className="pp-iframe-wrap">
                        <iframe
                          className="pp-media pp-iframe"
                          src={normalizeYoutube(m.src)}
                          title={`iframe-${iNon}`}
                          loading="lazy"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allow={iframeAllowFor(m.src)}
                          allowFullScreen
                          frameBorder="0"
                        />
                      </div>
                    ) : null}
                  </figure>
                );
              })}

              {/* one album card for all images */}
              {images.length > 0 && (
                <figure
                  className="pp-item album"
                  title="Open album"
                  onClick={() => {
                    if (firstImageIndex >= 0) {
                      setLbIndex(firstImageIndex);
                      setLbOpen(true);
                    }
                  }}
                >
                  <div className="pp-album-cover">
                    <img
                      src={images[0].src}
                      alt={`${title} — album cover`}
                      className="pp-media pp-album-img"
                      loading="lazy"
                    />
                    <div className="pp-album-shadow" aria-hidden="true" />
                    <div className="pp-album-logo">
                      <img
                        src="/images/circlelogo.png"
                        alt="Fenix Media 360"
                      />
                    </div>
                    <div className="pp-album-badge">{images.length} photos</div>
                  </div>
                  {/* <figcaption className="pp-album-caption">Photo Album</figcaption> */}
                </figure>
              )}
            </section>
          )}

          {/* Lightbox */}
          {lbOpen && (
            <LightboxModal
              show={lbOpen}
              onHide={() => setLbOpen(false)}
              title={title}
              media={media}
              startIndex={lbIndex}
              interval={6000}
            />
          )}

          {/* Bottom CTA */}
          <section className="pp-cta">
            <h2>Want results like this?</h2>
            <p>
              Let’s plan visuals that convert: 360° tours, drone, photo, and video.
            </p>
            <Link to="/contact">
              <Button
                className="btn-mobile"
                buttonStyle="btn--primary"
                buttonSize="btn--large"
              >
                Get a Quote
              </Button>
            </Link>
          </section>
        </>
      )}
    </main>
  );
}
